import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, FolderKanban, Pencil, Trash2, Search, CreditCard, ChevronDown, ChevronUp, Layers } from 'lucide-react';

const statusColor = { planning: 'badge-blue', in_progress: 'badge-yellow', review: 'badge-purple', completed: 'badge-green', on_hold: 'badge-gray' };

const CURRENCIES = {
  USD: { symbol: '$',    name: 'US Dollar',     flag: '' },
  EGP: { symbol: 'EGP ', name: 'Egyptian Pound', flag: '' },
  EUR: { symbol: 'EUR ',   name: 'Euro',           flag: '' },
  SAR: { symbol: 'SAR ', name: 'Saudi Riyal',    flag: '' },
  AED: { symbol: 'AED ', name: 'UAE Dirham',     flag: '' },
};

const ProjectModal = ({ onClose, onSave, clients, users, initial }) => {
  const isEdit = !!initial;
  const [activeTab, setActiveTab] = useState('details');
  const [projectTypes, setProjectTypes] = useState([]);
  const [selectedTypeServices, setSelectedTypeServices] = useState([]);
  const [form, setForm] = useState(
    initial
      ? {
          name: initial.name || '',
          description: initial.description || '',
          status: initial.status || 'planning',
          startDate: initial.startDate ? initial.startDate.split('T')[0] : '',
          endDate: initial.endDate ? initial.endDate.split('T')[0] : '',
          budget: initial.budget || '',
          clientId: initial.clientId || '',
          managerId: initial.managerId || '',
          projectTypeId: initial.projectTypeId || '',
        }
      : { name: '', description: '', status: 'planning', startDate: '', endDate: '', budget: '', clientId: '', managerId: '', projectTypeId: '' }
  );

  useEffect(() => {
    axios.get('/api/project-types').then(r => setProjectTypes(r.data.data)).catch(() => {});
  }, []);

  // Payment plan state
  const [enablePayment, setEnablePayment] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [installments, setInstallments] = useState([
    { description: 'First Payment', amount: '', dueDate: '' },
  ]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (k === 'projectTypeId') {
      const chosen = projectTypes.find(t => t.id === v);
      setSelectedTypeServices(chosen?.services || []);
    }
  };

  const sym = CURRENCIES[currency]?.symbol || '$';
  const totalInstallments = installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const budget = parseFloat(form.budget) || 0;
  const diff = budget - totalInstallments;

  const updateInstallment = (i, k, v) =>
    setInstallments(prev => prev.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addInstallment = () =>
    setInstallments(prev => [...prev, { description: `Payment ${prev.length + 1}`, amount: '', dueDate: '' }]);
  const removeInstallment = (i) =>
    setInstallments(prev => prev.filter((_, idx) => idx !== i));

  // Auto-split budget equally among installments
  const splitEvenly = () => {
    if (!budget || !installments.length) return;
    const each = (budget / installments.length).toFixed(2);
    setInstallments(prev => prev.map(inst => ({ ...inst, amount: each })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      budget: form.budget === '' ? null : form.budget,
      managerId: form.managerId === '' ? null : form.managerId,
      startDate: form.startDate === '' ? null : form.startDate,
      endDate: form.endDate === '' ? null : form.endDate,
    };
    try {
      let projectId;
      if (isEdit) {
        await axios.put(`/api/projects/${initial.id}`, payload);
        projectId = initial.id;
      } else {
        const r = await axios.post('/api/projects', payload);
        projectId = r.data.data?.id;
        // Copy template services from the selected project type
        if (projectId && selectedTypeServices.length) {
          await Promise.all(selectedTypeServices.map((s, i) =>
            axios.post(`/api/project-types/project/${projectId}/services`, { name: s.name, order: i })
          ));
        }
      }

      // Create invoice with payment plan if enabled (new projects only)
      if (!isEdit && enablePayment && form.clientId && installments.some(i => i.amount && i.dueDate)) {
        const validInstallments = installments.filter(i => i.amount && i.dueDate);
        const today = new Date().toISOString().split('T')[0];
        const lastDue = validInstallments.reduce((last, i) => i.dueDate > last ? i.dueDate : last, today);
        await axios.post('/api/invoices', {
          clientId: form.clientId,
          projectId,
          currency,
          issueDate: today,
          dueDate: lastDue,
          status: 'sent',
          items: [],
          installments: validInstallments,
          notes: `Payment plan for project: ${form.name}`,
        });
      }

      onSave();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Project' : 'New Project'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
          {[
            { key: 'details', label: 'Project Details' },
            { key: 'payment', label: 'Payment Plan' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none', border: 'none', padding: '12px 16px', fontSize: 13,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
                borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s',
              }}>
              {tab.label}
              {tab.key === 'payment' && enablePayment && (
                <span style={{ marginLeft: 6, background: 'var(--accent)', color: '#fff', borderRadius: 8, fontSize: 10, padding: '1px 6px' }}>
                  {installments.filter(i => i.amount).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>

            {/* ── Tab 1: Project Details ── */}
            {activeTab === 'details' && (
              <>
                <div className="input-group"><label className="input-label">Project Name *</label>
                  <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Brand Identity 2025" />
                </div>
                <div className="input-group"><label className="input-label">Client *</label>
                  <select className="input select" required value={form.clientId} onChange={e => set('clientId', e.target.value)}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                  </select>
                </div>
                <div className="grid-2">
                  <div className="input-group">
                    <label className="input-label">نوع المشروع</label>
                    <select className="input select" value={form.projectTypeId} onChange={e => set('projectTypeId', e.target.value)}>
                      <option value="">— بدون نوع —</option>
                      {projectTypes.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {/* Preview services for selected type */}
                    {selectedTypeServices.length > 0 && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Layers size={11} /> الخدمات المرفقة ({selectedTypeServices.length})
                        </div>
                        {selectedTypeServices.map(s => (
                          <div key={s.id} style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '2px 0' }}>• {s.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="input-group"><label className="input-label">Status</label>
                    <select className="input select" value={form.status} onChange={e => set('status', e.target.value)}>
                      {['planning', 'in_progress', 'review', 'completed', 'on_hold'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid-2">
                  <div className="input-group"><label className="input-label">Start Date</label>
                    <input type="date" className="input" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                  </div>
                  <div className="input-group"><label className="input-label">End Date</label>
                    <input type="date" className="input" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="input-group"><label className="input-label">Total Budget</label>
                    <input type="number" className="input" value={form.budget} onChange={e => set('budget', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="input-group"><label className="input-label">Manager</label>
                    <select className="input select" value={form.managerId} onChange={e => set('managerId', e.target.value)}>
                      <option value="">Unassigned</option>
                      {users.filter(u => ['admin', 'manager'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="input-group"><label className="input-label">Description</label>
                  <textarea className="input" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Project description..." />
                </div>
              </>
            )}

            {/* ── Tab 2: Payment Plan ── */}
            {activeTab === 'payment' && (
              <>
                {isEdit ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                    <CreditCard size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
                    <p style={{ fontSize: 13 }}>To manage payment plan, go to <strong>Finance</strong> and edit the invoice for this project.</p>
                  </div>
                ) : (
                  <>
                    {/* Enable toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: 10, marginBottom: 16, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CreditCard size={18} color="var(--accent)" />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>Enable Payment Plan</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Split the total into scheduled installments</div>
                        </div>
                      </div>
                      <button type="button"
                        onClick={() => setEnablePayment(p => !p)}
                        style={{
                          width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: enablePayment ? 'var(--accent)' : 'var(--border)',
                          position: 'relative', transition: 'background 0.2s',
                        }}>
                        <span style={{
                          position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
                          background: '#fff', transition: 'left 0.2s',
                          left: enablePayment ? 23 : 3,
                        }} />
                      </button>
                    </div>

                    {enablePayment && (
                      <>
                        {/* Currency */}
                        <div className="input-group">
                          <label className="input-label">Currency</label>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {Object.entries(CURRENCIES).map(([code, c]) => (
                              <button key={code} type="button" onClick={() => setCurrency(code)}
                                style={{
                                  padding: '7px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                  border: `1.5px solid ${currency === code ? 'var(--accent)' : 'var(--border)'}`,
                                  background: currency === code ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                                  color: currency === code ? 'var(--accent)' : 'var(--text-muted)',
                                  fontWeight: currency === code ? 600 : 400,
                                  display: 'flex', alignItems: 'center', gap: 5,
                                }}>
                                {c.flag} {code} <span style={{ opacity: 0.7 }}>({c.symbol})</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Budget summary */}
                        {form.budget && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                            <span style={{ color: 'var(--text-muted)' }}>Total Budget</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontWeight: 700 }}>{sym}{parseFloat(form.budget).toLocaleString()}</span>
                              <button type="button" onClick={splitEvenly} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }}>
                                Split Evenly
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Installments */}
                        <div className="input-group">
                          <label className="input-label">Installments</label>
                          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                            {/* Column headers */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8 }}>
                              {['Description', 'Amount', 'Due Date', ''].map(h => (
                                <div key={h} style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</div>
                              ))}
                            </div>

                            {installments.map((inst, i) => (
                              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                <input className="input" style={{ fontSize: 13, padding: '8px 10px' }}
                                  value={inst.description} placeholder={`Payment ${i + 1}`}
                                  onChange={e => updateInstallment(i, 'description', e.target.value)} />
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--text-muted)' }}>{sym}</span>
                                  <input className="input" style={{ fontSize: 13, padding: '8px 10px 8px 24px' }}
                                    type="number" min="0" step="0.01" value={inst.amount} placeholder="0.00"
                                    onChange={e => updateInstallment(i, 'amount', e.target.value)} />
                                </div>
                                <input className="input" style={{ fontSize: 13, padding: '8px 10px' }}
                                  type="date" value={inst.dueDate}
                                  onChange={e => updateInstallment(i, 'dueDate', e.target.value)} />
                                <button type="button" onClick={() => removeInstallment(i)}
                                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}>
                                  <X size={14} />
                                </button>
                              </div>
                            ))}

                            <button type="button" onClick={addInstallment} className="btn btn-secondary btn-sm" style={{ marginTop: 4 }}>
                              <Plus size={13} /> Add Installment
                            </button>
                          </div>
                        </div>

                        {/* Total check */}
                        {installments.some(i => i.amount) && (
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 14px', borderRadius: 8, fontSize: 13,
                            background: Math.abs(diff) < 0.01 ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)',
                            border: `1px solid ${Math.abs(diff) < 0.01 ? 'rgba(34,197,94,0.25)' : 'rgba(234,179,8,0.3)'}`,
                          }}>
                            <span style={{ color: 'var(--text-muted)' }}>Total Installments</span>
                            <div style={{ display: 'flex', align: 'center', gap: 12 }}>
                              <span style={{ fontWeight: 700 }}>{sym}{totalInstallments.toFixed(2)}</span>
                              {form.budget && Math.abs(diff) >= 0.01 && (
                                <span style={{ color: '#eab308', fontSize: 12 }}>
                                  {diff > 0 ? `${sym}${diff.toFixed(2)} remaining` : `${sym}${Math.abs(diff).toFixed(2)} over budget`}
                                </span>
                              )}
                              {Math.abs(diff) < 0.01 && form.budget && (
                                <span style={{ color: '#22c55e', fontSize: 12 }}>? Matches budget</span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Save Changes' : 'Create Project'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');

  // Sync filter with URL params (when navigating from Dashboard charts)
  React.useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setFilter(f);
  }, [searchParams]);
  const canManage = ['admin', 'manager'].includes(user?.role);

  const load = () => {
    Promise.all([
      axios.get('/api/projects'),
      canManage ? axios.get('/api/clients') : Promise.resolve({ data: { data: [] } }),
      canManage ? axios.get('/api/users') : Promise.resolve({ data: { data: [] } }),
    ]).then(([p, c, u]) => { setProjects(p.data.data); setClients(c.data.data); setUsers(u.data.data); }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const deleteProject = async (e, project) => {
    e.stopPropagation();
    if (!window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
    try { await axios.delete(`/api/projects/${project.id}`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete project'); }
  };

  const openEdit = (e, project) => {
    e.stopPropagation();
    setModal({ project });
  };

  const filtered = projects
    .filter(p => filter === 'all' || p.status === filter)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.client?.companyName?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && (
        <ProjectModal
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
          clients={clients}
          users={users}
          initial={modal.project || undefined}
        />
      )}
      <div className="flex-between mb-24">
        <div><h1 style={{ fontSize: 20, fontWeight: 700 }}>Projects</h1><p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{projects.length} total</p></div>
        {canManage && <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> New Project</button>}
      </div>

      <div className="search-bar">
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" style={{ paddingLeft: 36 }} placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['all', 'planning', 'in_progress', 'review', 'completed', 'on_hold'].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}>{s === 'all' ? 'All' : s.replace('_', ' ')}</button>
          ))}
        </div>
      </div>

      {!filtered.length ? <div className="empty-state"><FolderKanban /><p>No projects found</p></div> : (
        <div className="grid-3">
          {filtered.map(p => (
            <div key={p.id} className="card" onClick={() => navigate(`/projects/${p.id}`)}
              style={{ display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-light)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div className="flex-between">
                <span className={`badge ${statusColor[p.status] || 'badge-gray'}`}>{p.status?.replace('_', ' ')}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.projectType?.name || p.type?.replace('_', ' ') || ''}</span>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={e => openEdit(e, p)} title="Edit"
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 4px' }}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={e => deleteProject(e, p)} title="Delete"
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 4px' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div><div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div><div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>{p.client?.companyName}</div></div>
              {p.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{p.description}</p>}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 5 }}><span>Progress</span><span>{p.progress}%</span></div>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${p.progress}%` }} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                <span>{p.tasks?.length || 0} tasks</span>{p.endDate && <span>� Due {new Date(p.endDate).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;
